/**
 * What one tracker remembers between reads, and the transitions of that
 * memory: watching, anchored, lost, forgotten.
 *
 * The memory is what makes recovery possible. After every successful read it
 * holds the paragraphs the redline covered (their `uniqueLocalId`s for the
 * paragraph-id step, their `paraId`s and texts for the server), the text the
 * redline spanned (what every candidate is scored against), and the
 * neighbouring paragraphs seen over time (for the neighbour-id step). When the
 * bookmarks are lost it records the loss and each recovery attempt, and it
 * can describe the anchor to the server as a `SpanAnchor`, which is the same
 * shape the core's own resolver used to build from a baseline document.
 *
 * Underneath every read sits the redline itself: the text of the paragraphs
 * it was drafted against, and, on a card the core built with them, where each
 * of those paragraphs sat in the document at that time (`CardParagraphAnchor`:
 * its Word ids, its neighbours, the heading path over it, its index and
 * position). Those are the facts about the anchor that no edit to the document
 * can change, so they are what the memory falls back to when it has nothing
 * else, before the first successful read (the document was reopened after its
 * bookmarks were lost) and again after `forget`, when the user has asked for
 * the redline to be found afresh; a read that did happen still takes the
 * heading path, index and position from the card, which no read can tell.
 */
import { CONTEXT_WINDOW_WORDS, type NeighbourParagraph, type ParagraphAnchor, type SpanAnchor } from "@common/utils/redline/paragraph-locator";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { CardParagraphAnchor, CardParagraphNeighbour } from "@common/utils/redline/wire/redline-payload";

import type { RecoveryAttempt, RecoveryTrigger } from "./recovery/recovery-chain";
import type { RecoveryConfig } from "./recovery-config";
import type { BookmarkedRangeRead, WordParagraph } from "./word-document";

/** One paragraph the redline covered at the last successful read. */
export interface RememberedParagraph {
    uniqueLocalId: string;
    paraId: string | null;
    textId: string | null;
    allChangesAcceptedText: string;
}

/** One paragraph the redline was drafted against: its text before the author, and where the core's read placed it when the card was built, on a card that carries that. */
export interface DraftedParagraph {
    text: string;
    anchor: CardParagraphAnchor | null;
}

/**
 * The last CONTEXT_WINDOW_WORDS words of a text, joined by spaces: the context the
 * server compares before a paragraph.
 *
 * EXAMPLE
 *   words                 = [w0, w1, w2, w3, w4, w5, w6, w7, w8, w9, w10, w11]
 *   CONTEXT_WINDOW_WORDS  = 3
 *   slice(-3)             -> [w9, w10, w11]
 */
function lastContextWordsOf(text: string): string {
    return WordTokenizer.words(text).slice(-CONTEXT_WINDOW_WORDS).join(" ");
}

/**
 * The first CONTEXT_WINDOW_WORDS words of a text, joined by spaces: the context the
 * server compares after a paragraph.
 *
 * EXAMPLE
 *   words                 = [w0, w1, w2, w3, w4, w5, w6, w7, w8, w9, w10, w11]
 *   CONTEXT_WINDOW_WORDS  = 3
 *   slice(0, 3)           -> [w0, w1, w2]
 */
function firstContextWordsOf(text: string): string {
    return WordTokenizer.words(text).slice(0, CONTEXT_WINDOW_WORDS).join(" ");
}

/**
 * The neighbour at an edge of the span as the server reads it: the paragraph the
 * last read saw beside the span, carrying the ids the card remembered for that
 * side, since a read holds a neighbour as text alone; the card's neighbour alone
 * when no read saw one.
 *
 * CASES
 *   - the read saw a paragraph there      -> its text, with the card's ids for that side
 *   - it saw none, the card remembers one -> the card's neighbour as it was
 *   - neither                             -> null: the document's edge, as far as anyone knows
 */
function edgeNeighbourOf(readNeighbour: WordParagraph | null, cardNeighbour: CardParagraphNeighbour | null): NeighbourParagraph | null {
    if (readNeighbour === null) return cardNeighbour;
    return { paraId: cardNeighbour?.paraId ?? null, textId: cardNeighbour?.textId ?? null, text: readNeighbour.text };
}

export class AnchorMemory {
    private neighbourRing: WordParagraph[] = [];
    private primedFlag = false;
    private coveredParagraphs: RememberedParagraph[] = [];
    private spanText = "";
    /** The paragraphs the redline was drafted against, as `RedlineSource.textsBeforeAuthor` reads them, each with the anchor its card carries; survives `forget`. */
    private draftedParagraphs: DraftedParagraph[] = [];
    private paragraphBefore: WordParagraph | null = null;
    private paragraphAfter: WordParagraph | null = null;
    private watchingSinceMs = Date.now();
    private lostAtMs: number | null = null;
    private lastRecoveryAtMs: number | null = null;
    private readonly attemptHistory: RecoveryAttempt[] = [];

    constructor(private readonly config: RecoveryConfig) {}

    /** A first successful read has happened; until then every reconcile takes the write lock. */
    get primed(): boolean {
        return this.primedFlag;
    }

    /** Paragraph ids from the last successful read: the first recovery step's input. */
    get lastUniqueLocalIds(): string[] {
        return this.coveredParagraphs.map((paragraph) => paragraph.uniqueLocalId);
    }

    /**
     * The text candidates are scored against: what the redline covered at the
     * last read, or, when nothing has been read since the tracker started or
     * since `forget`, the text the redline was drafted against.
     */
    get lastSpanText(): string {
        return this.spanText.length > 0 ? this.spanText : this.draftedParagraphs.map((paragraph) => paragraph.text).join(" ");
    }

    /**
     * The text of each paragraph the redline covers, one entry per paragraph, for
     * the step that finds the redline by text alone: what the paragraphs read at
     * the last read, or, when nothing has been read since the tracker started or
     * since `forget`, the paragraphs the redline was drafted against.
     */
    lastParagraphTexts(): string[] {
        if (this.coveredParagraphs.length > 0) return this.coveredParagraphs.map((paragraph) => paragraph.allChangesAcceptedText);
        return this.draftedParagraphs.map((paragraph) => paragraph.text);
    }

    /** Neighbouring paragraphs seen over the tracker's life, oldest first. */
    get neighbours(): readonly WordParagraph[] {
        return this.neighbourRing;
    }

    /** When this tracker started watching; ledger entries older than this are ignored. */
    get watchingSince(): number {
        return this.watchingSinceMs;
    }

    get isLost(): boolean {
        return this.lostAtMs !== null;
    }

    neighbourTexts(): string[] {
        return this.neighbourRing.map((entry) => entry.text);
    }

    /**
     * The redline this tracker follows, as the paragraphs it was drafted
     * against, each with the anchor its card carries for it. Blank paragraphs
     * are dropped with their anchors: a wholly inserted paragraph has no text
     * before its author and so names no paragraph to look for.
     *
     * WHY by index
     *   `RedlineSource.textsBeforeAuthor` lists a text card's paragraphs in the
     *   card's order, so the anchor at the same index is that paragraph's. A
     *   markup card carries no anchors, so every paragraph of it gets null.
     *
     * EXAMPLE
     *   textsBeforeAuthor  ["Pay in 30 days.", "", "Late fees accrue."]
     *   cardAnchors        [anchorA, null, anchorC]
     *   drafted            [{ text "Pay in 30 days.", anchorA }, { text "Late fees accrue.", anchorC }]
     */
    rememberRedlineTexts(textsBeforeAuthor: string[], cardAnchors: (CardParagraphAnchor | null)[]): void {
        this.draftedParagraphs = textsBeforeAuthor
            .map((text, index) => ({ text, anchor: cardAnchors[index] ?? null }))
            .filter((paragraph) => paragraph.text.trim().length > 0);
    }

    /** Adds the paragraphs either side of a read to the ring of remembered neighbours, most recent last. */
    remember(read: BookmarkedRangeRead): void {
        const seen = new Map(this.neighbourRing.map((entry) => [entry.uniqueLocalId, entry]));
        for (const entry of [read.previousParagraph, read.nextParagraph]) {
            const entryHasValue = entry !== null;
            const entryHasRememberedText = (): boolean => {
                if (!entryHasValue) return false;
                return entry.text.trim().length > 0;
            };
            /**
             * CASES
             *   - entry is null          -> document edge, remember nothing
             *   - entry text is blank    -> no neighbour text worth keeping
             *   - otherwise              -> store by uniqueLocalId
             */
            if (entryHasRememberedText()) seen.set(entry.uniqueLocalId, entry);
        }
        /**
         * WHY
         *   Keep only the most recent remembered neighbours, capped by
         *   this.config.neighbourMemory.
         *
         * EXAMPLE
         *   seen values      = [0, 1, 2, 3, 4, 5, 6]
         *   neighbourMemory  = 3
         *   slice(-3)        -> [4, 5, 6]
         */
        this.neighbourRing = Array.from(seen.values()).slice(-this.config.neighbourMemory);
    }

    /** A successful read: the redline is anchored on these paragraphs and covers `spanText` of them. */
    anchored({ read, paragraphTexts, spanText }: { read: BookmarkedRangeRead; paragraphTexts: string[]; spanText: string }): void {
        this.primedFlag = true;
        this.lostAtMs = null;
        this.coveredParagraphs = read.uniqueLocalIds.map((uniqueLocalId, index) => ({
            uniqueLocalId,
            paraId: read.paragraphIds[index]?.paraId ?? null,
            textId: read.paragraphIds[index]?.textId ?? null,
            allChangesAcceptedText: paragraphTexts[index] ?? "",
        }));
        this.paragraphBefore = read.previousParagraph;
        this.paragraphAfter = read.nextParagraph;
        this.spanText = spanText;
    }

    /** A read found no bookmarks. Returns true on the first loss, when recovery runs at once regardless of trigger. */
    markLost(): boolean {
        if (this.lostAtMs !== null) return false;
        this.lostAtMs = Date.now();
        return true;
    }

    /** The first loss and every user action recover at once; heartbeats retry at most once per heartbeat window. */
    recoveryDue(trigger: RecoveryTrigger): boolean {
        if (this.lostAtMs === null) return true;
        /**
         * CASES
         *   - trigger is not "heartbeat"  -> recover now
         *   - trigger is "heartbeat"      -> throttle by heartbeat window
         */
        if (trigger !== "heartbeat") return true;
        return this.lastRecoveryAtMs === null || Date.now() - this.lastRecoveryAtMs >= this.config.heartbeatMs;
    }

    record(attempt: RecoveryAttempt): void {
        this.lastRecoveryAtMs = attempt.attemptedAtMs;
        this.attemptHistory.push(attempt);
        /**
         * WHEN attemptHistory grows past this.config.attemptHistory
         *   CONSEQUENCE
         *     Drop the oldest attempt so the array stays capped.
         *
         * EXAMPLE
         *   limit               = 5
         *   after push          = [0, 1, 2, 3, 4, 5]
         *   shift() removes     -> 0
         *   kept entries        -> [1, 2, 3, 4, 5]
         */
        if (this.attemptHistory.length > this.config.attemptHistory) this.attemptHistory.shift();
    }

    lastAttempt(): RecoveryAttempt | null {
        /**
         * RETURNS
         *   The newest recorded attempt: the last array entry.
         *
         * EXAMPLE
         *   attemptHistory  = [a0, a1, a2]
         *   length - 1      = 2
         *   returns         -> a2
         *
         * NULL
         *   No attempts are recorded yet. Caller should treat this as
         *   "no last attempt available".
         */
        return this.attemptHistory[this.attemptHistory.length - 1] ?? null;
    }

    /**
     * Drops everything read from the document, keeping the attempt history
     * and the redline's own paragraphs with their card anchors; the redline is
     * lost until the next recovery, which looks for the paragraphs the redline
     * was drafted against, by the ids and neighbours the card remembers first.
     */
    forget(): void {
        this.neighbourRing = [];
        this.primedFlag = false;
        this.coveredParagraphs = [];
        this.spanText = "";
        this.paragraphBefore = null;
        this.paragraphAfter = null;
        this.watchingSinceMs = Date.now();
        this.lostAtMs = Date.now();
    }

    /**
     * The anchor as the server resolver wants it: one member per covered
     * paragraph, each with its ids, its text, the words either side of it, its
     * neighbours, and the heading path, index and position the card remembers.
     *
     * CASES
     *   - a read has described the span       -> `membersFromRead`: the read's ids and
     *                                            texts, the card's heading, index and position
     *   - no read, the redline's own paragraphs -> `membersFromCard`: the card's anchors where it
     *                                            carries them, the texts as drafted otherwise
     *   - neither                             -> one text-only member for the whole span
     */
    toSpanAnchor(): SpanAnchor {
        if (this.coveredParagraphs.length > 0) return { members: this.membersFromRead() };
        if (this.draftedParagraphs.length > 0) return { members: this.membersFromCard() };
        return { members: [this.wholeSpanMember()] };
    }

    /**
     * The members from the last read: context words come from the adjoining
     * covered paragraph or, at the edges, from the paragraphs that sat before and
     * after the range; the heading path, index and position come from the card's
     * anchor for the same paragraph, which no read can tell.
     */
    private membersFromRead(): ParagraphAnchor[] {
        const paragraphs = this.coveredParagraphs;
        return paragraphs.map((paragraph, index) => {
            /**
             * CASES
             *   - index > 0      -> use paragraphs[index - 1]
             *   - index === 0    -> null; no covered paragraph above
             *
             * EXAMPLE
             *   index = 2        -> paragraphs[1]
             *   index = 0        -> null
             */
            const above = index > 0 ? paragraphs[index - 1] : null;
            /**
             * CASES
             *   - index + 1 is in range   -> use paragraphs[index + 1]
             *   - index is last entry     -> null; no covered paragraph below
             *
             * EXAMPLE
             *   paragraphs.length = 5, index = 2  -> paragraphs[3]
             *   paragraphs.length = 5, index = 4  -> null
             */
            const below = index + 1 < paragraphs.length ? paragraphs[index + 1] : null;
            const cardAnchor = this.cardAnchorOfCoveredParagraph(index);
            return {
                paraId: paragraph.paraId,
                textId: paragraph.textId,
                text: paragraph.allChangesAcceptedText,
                contextBefore: lastContextWordsOf(above ? above.allChangesAcceptedText : this.paragraphBefore?.text ?? ""),
                contextAfter: firstContextWordsOf(below ? below.allChangesAcceptedText : this.paragraphAfter?.text ?? ""),
                headingPath: cardAnchor?.headingPath ?? null,
                paragraphIndex: cardAnchor?.paragraphIndex ?? null,
                positionPct: cardAnchor?.positionPct ?? null,
                /**
                 * CASES
                 *   - above exists    -> previous covered paragraph is the neighbour
                 *   - otherwise       -> the paragraph before the range, with the card's ids for that side
                 */
                neighbourAbove: above ? { paraId: above.paraId, textId: above.textId, text: above.allChangesAcceptedText } : edgeNeighbourOf(this.paragraphBefore, cardAnchor?.neighbourAbove ?? null),
                /**
                 * CASES
                 *   - below exists    -> next covered paragraph is the neighbour
                 *   - otherwise       -> the paragraph after the range, with the card's ids for that side
                 */
                neighbourBelow: below ? { paraId: below.paraId, textId: below.textId, text: below.allChangesAcceptedText } : edgeNeighbourOf(this.paragraphAfter, cardAnchor?.neighbourBelow ?? null),
            };
        });
    }

    /**
     * The card's anchor for the covered paragraph at `index`, when the card lists
     * the covered paragraphs one to one.
     *
     * NULL
     *   The card lists a different number of paragraphs than the read covers (a
     *   blank line the card holds and the drafted list dropped, a range that grew),
     *   so no index names the same paragraph on both sides; or the card carries no
     *   anchor for it.
     */
    private cardAnchorOfCoveredParagraph(index: number): CardParagraphAnchor | null {
        if (this.draftedParagraphs.length !== this.coveredParagraphs.length) return null;
        return this.draftedParagraphs[index].anchor;
    }

    /**
     * The members from the redline's own paragraphs, when nothing has been read:
     * one per drafted paragraph, each taking its ids, heading path, index and
     * position from the card's anchor where the card carries one, and its context
     * and neighbours from the drafted paragraphs beside it or, at the edges, from
     * the card's remembered neighbours. A card without anchors sends texts alone,
     * the way the resolver once anchored a redline that arrived without a baseline
     * document.
     */
    private membersFromCard(): ParagraphAnchor[] {
        const drafted = this.draftedParagraphs;
        /** A drafted paragraph as the neighbour of the one beside it: its text, and the ids its card anchor carries. */
        const neighbourOfDrafted = (paragraph: DraftedParagraph): NeighbourParagraph => ({ paraId: paragraph.anchor?.paraId ?? null, textId: paragraph.anchor?.textId ?? null, text: paragraph.text });
        return drafted.map((paragraph, index) => {
            /**
             * CASES
             *   - index > 0      -> use drafted[index - 1]
             *   - index === 0    -> null; no drafted paragraph above
             *
             * EXAMPLE
             *   index = 2        -> drafted[1]
             *   index = 0        -> null
             */
            const above = index > 0 ? drafted[index - 1] : null;
            /**
             * CASES
             *   - index + 1 is in range   -> use drafted[index + 1]
             *   - index is last entry     -> null; no drafted paragraph below
             *
             * EXAMPLE
             *   drafted.length = 4, index = 2  -> drafted[3]
             *   drafted.length = 4, index = 3  -> null
             */
            const below = index + 1 < drafted.length ? drafted[index + 1] : null;
            const anchor = paragraph.anchor;
            const neighbourAbove = above !== null ? neighbourOfDrafted(above) : edgeNeighbourOf(this.paragraphBefore, anchor?.neighbourAbove ?? null);
            const neighbourBelow = below !== null ? neighbourOfDrafted(below) : edgeNeighbourOf(this.paragraphAfter, anchor?.neighbourBelow ?? null);
            return {
                paraId: anchor?.paraId ?? null,
                textId: anchor?.textId ?? null,
                text: paragraph.text,
                contextBefore: lastContextWordsOf(neighbourAbove?.text ?? ""),
                contextAfter: firstContextWordsOf(neighbourBelow?.text ?? ""),
                headingPath: anchor?.headingPath ?? null,
                paragraphIndex: anchor?.paragraphIndex ?? null,
                positionPct: anchor?.positionPct ?? null,
                neighbourAbove,
                neighbourBelow,
            };
        });
    }

    /** One text-only member for the whole span: the last fallback, when neither a read nor the redline's paragraphs describe it. */
    private wholeSpanMember(): ParagraphAnchor {
        return {
            paraId: null,
            textId: null,
            text: this.spanText,
            contextBefore: lastContextWordsOf(this.paragraphBefore?.text ?? ""),
            contextAfter: firstContextWordsOf(this.paragraphAfter?.text ?? ""),
            headingPath: null,
            paragraphIndex: null,
            positionPct: null,
            neighbourAbove: edgeNeighbourOf(this.paragraphBefore, null),
            neighbourBelow: edgeNeighbourOf(this.paragraphAfter, null),
        };
    }
}
